import { LANGUAGE_LABELS, type Language } from '@voxa/shared';

/**
 * How a language is written in a picker: what it calls itself, and — when that
 * is a different word — what the reader's own language calls it.
 *
 * Both halves earn their place. The endonym is what someone scans for when
 * choosing their own language, because it is the word they already know. The
 * reader's word is what they need when choosing for somebody else: picking
 * `Nederlands` for the other person means knowing it is Dutch.
 *
 * The second half is not translated by hand. `Intl.DisplayNames` is in the
 * browser, knows every pair, and gets the conventions right on its own — Russian
 * and French write language names in lower case, English capitalises them.
 * Eight languages squared is sixty-four strings nobody would keep correct.
 */

// Constructing one is not free and the picker re-renders; one per interface
// language is plenty.
const cache = new Map<string, Intl.DisplayNames | null>();

function displayNames(ui: string): Intl.DisplayNames | null {
  const hit = cache.get(ui);
  if (hit !== undefined) return hit;

  let made: Intl.DisplayNames | null = null;
  try {
    made = new Intl.DisplayNames([ui], { type: 'language' });
  } catch {
    // An engine without the data for this locale. The endonym alone is still
    // a correct answer, so this is a missing nicety rather than a failure.
  }
  cache.set(ui, made);
  return made;
}

/**
 * Isolate the endonym from the text around it. In a Hebrew interface the two
 * halves run in opposite directions, and without this the brackets end up on
 * the wrong side of the word they belong to.
 */
const isolate = (text: string) => `⁨${text}⁩`;

function nameInReadersLanguage(code: Language, ui: string): string | null {
  try {
    // `of` returns the code back when it has no name for it.
    const name = displayNames(ui)?.of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

/** A language name used as a value in a sentence in the current UI language. */
export function localizedLanguageName(code: Language, ui: string): string {
  const name = nameInReadersLanguage(code, ui) ?? LANGUAGE_LABELS[code];
  return name.charAt(0).toLocaleUpperCase(ui) + name.slice(1);
}

export function languageOptionLabel(code: Language, ui: string): string {
  const endonym = LANGUAGE_LABELS[code];
  const inReadersLanguage = nameInReadersLanguage(code, ui);
  if (inReadersLanguage === null) return endonym;

  // A language named in itself in its own interface: `Deutsch (Deutsch)` is
  // noise. Compared without case, because most of these differ only by it —
  // `Français` against `français`.
  if (inReadersLanguage.toLocaleLowerCase(ui) === endonym.toLocaleLowerCase(ui)) return endonym;

  return `${isolate(endonym)} (${isolate(inReadersLanguage)})`;
}
